import { Button, Modal, SearchInput, useToastMessage } from 'ccs-ui-components';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import useLoader from '../../../common/hooks/Loader';
import { deleteScheduler } from '../../store/_api';
import classes from './SchedulerModal.module.scss';
import { CreateSchedule } from './scheduler-components/create-schedule/CreateSchedule';
import { ScheduleList } from './scheduler-components/schedule-list/ScheduleList';
import { ScheduleProps } from './utilities/TypeDefs';

export const SchedulerModal = (props: ScheduleProps) => {
  const { schedulerModalFlag, closeSchedulerModal, journeyName, journeyStepName, eventName, eventVersion, journeyVersion } = props;

  // state variables
  const [createJobFlag, setCreateJobFlag] = useState<boolean>(false);
  const [searchVal, setSearchVal] = useState<string>('');
  const [showToast, setShowToast] = useState<boolean>(false);
  const [page, setPage] = useState<number>(0);
  const [mode, setMode] = useState<string>('create');

  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [detailsForJobToBeDeleted, setDetailsForJobToBeDeleted] = useState<any>({});

  // hooks
  const { t: translate } = useTranslation();
  const toastMessage = useToastMessage();
  const loader = useLoader();

  /**
   * @method createJobBtn
   * @description navigates the user to create Job schedule page
   */
  const createJobBtn = () => {
    setCreateJobFlag(true);
    setMode('create');
  };

  /**
   * @method searchHandler
   * @description handles the search item inside the list
   * @param e
   */
  const searchHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPage(0);
    setSearchVal(e.target.value);
  };

  /**
   * @name modalListTitle
   * @description contains the list schedule title
   */
  const modalListTitle = (
    <div className={classes['job-list-title']} data-testid="modalTitle">
      <p><span>{translate('job_scheduler_component.job_list_title')}</span>{journeyName}</p>
      <p className={classes.description}>{translate('job_scheduler_component.txt_description')}</p>
    </div>
  );

  /**
   * @name modalCreateScheduleTitle
   * @description contains the create schedule title
   */
  const modalScheduleTitle = (
    <div className={classes['create-job-title']} data-testid="createTitle">
      <p>
        <button aria-label="back" type="button" onClick={() => { setCreateJobFlag(false); }} data-testid="backBtn"><i className="bi bi-arrow-left" /> </button>
        {mode === 'create' ? translate('job_scheduler_component.create_job_title') : null}
        {mode === 'view' ? translate('job_scheduler_component.view_job_title') : null}
        {mode === 'edit' ? translate('job_scheduler_component.edit_job_title') : null}
      </p>
    </div>
  );

  const deleteSchedule = (journeyName: string, jobName: string) => {
    setShowDeleteModal(true);
    setDetailsForJobToBeDeleted({ journeyName, jobName });
  };

  /**
   * @name modalBody
   * @description contains the list schedule body
   */
  const modalBody = (
    <>
      {toastMessage.renderToast()}
      <div className={classes['list-container']} data-testid="modalBody">
        <div className={classes['job-list-body']}>
          <div className={classes.search}>
            <SearchInput
              placeholder={translate('job_scheduler_component.txt_search_job_name') ?? ''}
              label="search"
              type="search"
              value={searchVal}
              onChange={searchHandler}
              data-testid="listSearch"
            />
          </div>
          <Button variant="primary" onClick={createJobBtn}>{translate('job_scheduler_component.btn_txt_create')}</Button>
        </div>
        <ScheduleList flag={createJobFlag} deleteScheduler={deleteSchedule} searchTerm={searchVal} journeyName={journeyName} page={page} setPage={setPage} journeyVersion={journeyVersion} />
      </div>
    </>
  );

  /**
  * @method closeModalHandler
  * @description close the modal
  */
  const closeModalHandler = () => {
    setSearchVal('');
    closeSchedulerModal();
  };

  const deleteJobScheduler = async () => {
    loader.show();
    const resp = await deleteScheduler(detailsForJobToBeDeleted.journeyName, detailsForJobToBeDeleted.jobName);
    loader.hide();
    if (resp) {
      toastMessage.success(translate('job_scheduler_component.title_toast_success_header'), translate('job_scheduler_component.msg_delete_api_successful'));
    } else {
      toastMessage.failure(translate('job_scheduler_component.title_toast_error_header'), translate('job_scheduler_component.msg_delete_api_error'));
    }
    setDetailsForJobToBeDeleted({});
    setShowDeleteModal(false);
  };

  const cancelDeleteJobScheduler = () => {
    setDetailsForJobToBeDeleted({});
    setShowDeleteModal(false);
  };

  useEffect(() => {
    if (showToast) {
      toastMessage.success(
        translate('job_scheduler_component.title_toast_success_header'),
        translate('job_scheduler_component.api_successful')
      );
      setShowToast(false);
    }
  }, [showToast]);

  return (
    <>
      {loader.render()}
      {toastMessage.renderToast()}
      {
        !showDeleteModal && (
          <Modal
            modalTitle={createJobFlag ? modalScheduleTitle : modalListTitle}
            modalBody={createJobFlag ? <CreateSchedule setCancel={(e) => { setCreateJobFlag(e); }} journeyName={journeyName} journeyStepName={journeyStepName} eventName={eventName} eventVersion={eventVersion} setApiSuccess={setShowToast} journeyVersion={journeyVersion} /> : modalBody}
            modalShow={schedulerModalFlag}
            onCloseModal={closeModalHandler}
            alignToCentre
            dataTestId="schedulerModal"
            className="modal-lg"
            closeBtnShow={!createJobFlag}
          />
        )
      }
      {
        showDeleteModal && (
          <Modal
            modalTitle={`${translate('job_scheduler_component.title_delete_job')}`}
            modalBody={`${translate('job_scheduler_component.desc_delete_job')}`}
            modalShow={showDeleteModal}
            dataTestId="delete-job-modal"
            btnCloseText={`${translate('job_scheduler_component.lbl_btn_cancel_job')}`}
            btnSaveText={`${translate('job_scheduler_component.lbl_btn_delete_job')}`}
            onCloseModal={cancelDeleteJobScheduler}
            onSaveModal={deleteJobScheduler}
            alignToCentre
            disableSaveBtn={false}
            disableCloseBtn={false}
            type="warning"
          />
        )
      }
    </>

  );
};
